import { FC, useEffect, useRef, useState } from 'react'
import { cls } from '@utils/classnames'

import { isMobile } from '../../../utils/mediaQueries'
import { ProductUnitModal } from '../ProductUnitModal/ProductUnitModal'

type ProductUnitMachinesProperties = {
  selectMachineLabel: string
  machines: string[]
  selectedMachine: string
  onMachineSelect: (machine: string) => void
  applyButtonLabel: string
  resetButtonLabel: string
  clearAriaLabel: string
}

export const ProductUnitMachines: FC<ProductUnitMachinesProperties> = ({
  selectMachineLabel,
  machines,
  selectedMachine,
  onMachineSelect,
  applyButtonLabel,
  resetButtonLabel,
  clearAriaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const reference = useRef<HTMLDivElement>(null)
  const isMobileView = isMobile()

  useEffect(() => {
    if (!isOpen || isMobileView) return
    const handleClickOutside = (event: MouseEvent) => {
      if (reference.current && !reference.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isMobile])

  useEffect(() => {
    if (!isMobile) return
    if (isOpen) {
      const previous = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = previous
      }
    }
  }, [isOpen, isMobile])

  const handleSelect = (machine: string) => {
    onMachineSelect(machine)
    if (!isMobile) setIsOpen(false)
  }

  const clearSelection = () => {
    onMachineSelect('')
  }

  const handleApply = () => {
    setIsOpen(false)
  }

  const handleKeyboardSelection = (machine: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect(machine)
    }
  }

  return (
    <div className="cmp-product-units__machines-select" ref={reference}>
      <button
        className={cls({
          'cmp-product-units__machine-selector': true,
          'cmp-product-units__machine-selector--is-selected': Boolean(selectedMachine),
        })}
        onClick={() => setIsOpen((previous) => !previous)}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedMachine || selectMachineLabel}

        {selectedMachine && !isOpen ? (
          <button
            className="cmp-product-units__machine-selector__close"
            onClick={(event) => {
              event.stopPropagation()
              clearSelection()
            }}
            type="button"
            aria-label={clearAriaLabel}
          />
        ) : (
          <span
            className={cls({
              'cmp-product-units__machine-selector__arrow': true,
              'cmp-product-units__machine-selector__arrow--is-open': isOpen,
            })}
            role="presentation"
          />
        )}
      </button>

      {isOpen && !isMobileView && (
        <ul className="cmp-product-units__machine-options" role="listbox">
          {machines.map((machine, index) => (
            <li
              key={machine}
              onClick={() => handleSelect(machine)}
              onKeyDown={handleKeyboardSelection(machine)}
              className={cls({
                'cmp-product-units__machine-option': true,
                'cmp-product-units__machine-option--is-active': selectedMachine === machine,
                first: index === 0,
                'cmp-product-units__machine-option--is-last': index === machines.length - 1,
              })}
              role="option"
              aria-selected={selectedMachine === machine}
              tabIndex={0}
            >
              <span className="option-label">{machine}</span>
              {selectedMachine === machine && (
                <span className="cmp-product-units__machine-option-checkmark" aria-hidden="true" />
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen && isMobileView && (
        <ProductUnitModal
          title={selectMachineLabel}
          onClose={() => setIsOpen(false)}
          primaryButtonLabel={applyButtonLabel}
          primaryButtonOnClick={handleApply}
          secondaryButtonLabel={resetButtonLabel}
          secondaryButtonOnClick={clearSelection}
        >
          <ul className="cmp-product-units__machine-modal__list" role="listbox">
            {machines.map((machine) => (
              <li
                key={machine}
                className={cls({
                  'cmp-product-units__machine-modal__item': true,
                  'cmp-product-units__machine-modal__item--is-active': selectedMachine === machine,
                })}
                onClick={() => handleSelect(machine)}
                onKeyDown={handleKeyboardSelection(machine)}
                role="option"
                aria-selected={selectedMachine === machine}
                tabIndex={0}
              >
                <span>{machine}</span>
                {selectedMachine === machine && (
                  <span className="cmp-product-units__machine-modal-checkmark" aria-hidden="true"></span>
                )}
              </li>
            ))}
          </ul>
        </ProductUnitModal>
      )}
    </div>
  )
}
